import { useNavigate, useSearchParams } from "@solidjs/router";
import DOMPurify from "dompurify";
import { marked } from "marked";
import { createMemo, createSignal, Show } from "solid-js";
import { toast } from "solid-sonner";
import { useI18n } from "@/api/i18n";
import { getApiErrorMessage, getApiValidationMessage } from "@/api/utils/error";
import { Button } from "@/components/ui/button";
import { useAuthV2 } from "@/domains/auth/context/auth-provider";
import type { PlanLimitError } from "@/domains/billing/api/types";
import { isPlanLimitPayload } from "@/domains/billing/utils/plan-limit";
import {
  useGetCompanyV2,
  useGetUserCompaniesV2,
} from "@/domains/company/company/api";
import { selectCompanyById } from "@/domains/company/company/api/selectors";
import { useCompanyStore } from "@/domains/company/company/store/company-store";
import { usePostHog } from "@/hooks/usePostHog";
import { DetailContent } from "../../../../components/detail-sheet";
import { useCreateJobOfferV2 } from "../../../../create/api";
import type { JobOfferV2WithCompanyId } from "../../../../types";
import { useAiChatStore } from "../../../store/AiChatProvider";
import { useRefineStore } from "../../store/RefineProvider";
import { toJobOfferInsert } from "../../utils/job-offer-insert";
import { previewSubcategoryField } from "../../utils/offer-subcategories";
import { validateRefineState } from "../../validators";

export function FinalPreviewStep() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { capture } = usePostHog();
  const { state } = useRefineStore();
  const aiChat = useAiChatStore();
  const [companyStore] = useCompanyStore();
  const [authStore] = useAuthV2();
  const companyId = () => companyStore.selectedCompany?.id ?? "";
  /** Public DTO — what the preview renders: name, logo, industry. Nothing fiscal. */
  const companyQuery = useGetCompanyV2(companyId);
  /**
   * Authenticated read, for `invoice_vat` alone.
   *
   * The public DTO of `GET /company/:id` is about to stop carrying the fiscal
   * fields (AC 22), and it should never have carried them: they travel on an
   * unauthenticated route. This is the only place left in the app that read one
   * from there.
   */
  const companiesQuery = useGetUserCompaniesV2();
  const invoiceVat = () =>
    selectCompanyById(companiesQuery.data?.companies, companyId())?.invoice_vat;
  const isRefineStateValid = createMemo(
    () => validateRefineState(state).success,
  );
  const createJobOffer = useCreateJobOfferV2();
  const [planLimitError, setPlanLimitError] =
    createSignal<PlanLimitError | null>(null);

  const sanitizedDescription = createMemo(() => {
    const html = state.description
      ? (marked.parse(state.description, { async: false }) as string)
      : "";
    return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
  });

  const previewJobOffer = (): JobOfferV2WithCompanyId | null => {
    if (!companyQuery.data?.company) return null;
    const company = companyQuery.data.company;

    return {
      id: "preview",
      title: state.title,
      description: sanitizedDescription(),
      payment_status: "pending",
      remote_policy: state.remote_policy!,
      start_date: state.start_date ?? undefined,
      renewable: state.renewable,
      experience_level: state.experience_level ?? undefined,
      skills: state.skills,
      daily_rate_min: state.daily_rate_min ?? undefined,
      daily_rate_max: state.daily_rate_max ?? undefined,
      budget: state.budget ?? undefined,
      currency: state.currency,
      location: state.location ? [state.location] : undefined,
      payment_type: state.payment_type ?? undefined,
      effort: state.effort ?? undefined,
      duration: state.duration ?? undefined,
      job_category: state.job_category!,
      job_subcategory: previewSubcategoryField(
        state.job_category,
        state.subcategories,
      ),
      internal_ats: state.internal_ats,
      apply_url: state.apply_url || undefined,
      calendar_url: state.calendar_url || undefined,
      is_active: true,
      applications_count: 0,
      views_count: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      company_id: {
        id: company.id,
        name:
          company.kind === "solo_freelancer"
            ? (authStore.profile?.full_name ?? company.name)
            : company.name,
        logo_url:
          company.kind === "solo_freelancer"
            ? (authStore.profile?.avatar_url ?? company.logo_url)
            : company.logo_url,
        kind: company.kind,
        status: company.status,
        onboarding_status: company.onboarding_status,
        archived_at: company.archived_at,
      },
    };
  };

  const onSubmit = async () => {
    const company = companyQuery.data?.company;
    if (!company) return;

    setPlanLimitError(null);
    const from = searchParams.from;

    const body = {
      company: {
        id: company.id,
        invoice_vat: invoiceVat(),
      },
      job_offer: toJobOfferInsert(state, sanitizedDescription(), from),
    };

    await createJobOffer
      .mutateAsync(body)
      .then((response) => {
        aiChat.reset();
        capture("end_create_job_offer");
        if (from === "ai_generation") {
          toast.success(t("jobOffers.refine.ai_publish_success"));
          capture("create_job_offer_from_ai_generation");
        } else {
          toast.success(t("jobOffers.refine.publishSuccess"));
        }

        const nextParam = searchParams.next;
        if (nextParam) {
          const decoded = decodeURIComponent(String(nextParam));
          const separator = decoded.includes("?") ? "&" : "?";
          const url = `${decoded}${separator}job_offer_id=${response.job_offer.id}`;

          navigate(url, { replace: true });
        } else {
          localStorage.setItem("created_job_offer_id", response.job_offer.id);
          navigate("/job-offers/create/success");
        }
      })
      .catch((error) => {
        const data = (error as { response?: { data?: unknown } })?.response
          ?.data;
        if (isPlanLimitPayload(data)) {
          setPlanLimitError(data);
          return;
        }
        toast.error(
          getApiValidationMessage(error) ??
            getApiErrorMessage(error, t("jobOffers.refine.publishError"), t),
        );
      });
  };

  return (
    <div class="space-y-6">
      <div>
        <h3 class="text-lg font-semibold mb-1">
          {t("jobOffers.refine.steps.finalPreview")}
        </h3>
        <p class="text-sm text-muted-foreground">
          {t("jobOffers.refine.finalPreviewDescription")}
        </p>
      </div>

      <Show
        when={companyQuery.isSuccess && previewJobOffer()}
        fallback={
          <div class="p-4 border rounded-lg">
            <Show
              when={companyQuery.isLoading}
              fallback={<p>{t("jobOffers.refine.companyLoadError")}</p>}
            >
              <p>{t("jobOffers.refine.loadingCompany")}</p>
            </Show>
          </div>
        }
      >
        <div class="bg-white rounded-sm border p-4">
          <DetailContent
            job={previewJobOffer()!}
            isAlreadyApplied={false}
            preview
          />
        </div>
      </Show>

      {/* 403 plan_limit_exceeded is surfaced once by the global PlanLimitModal
          (`PlanLimitWatcher`, mounted in the router). We keep `planLimitError`
          only to hold the submit button disabled after a limit hit — no inline
          banner here (D11). */}

      <div class="flex justify-end gap-4 items-center">
        <Button
          onClick={onSubmit}
          disabled={
            createJobOffer.isPending ||
            companyQuery.isLoading ||
            companyQuery.isError ||
            !isRefineStateValid() ||
            !!planLimitError()
          }
        >
          {createJobOffer.isPending
            ? t("common.saving")
            : t("jobOffers.refine.publish")}
        </Button>
      </div>
    </div>
  );
}
